import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import AuthTemplate from './authTemplate';
import Input from '../ui/forms/fields/input';
import PrimaryButton from '../ui/buttons/primaryButton';
import { useAlert } from '../../contexts/alertContext';
import userService from '../../features/user/userService';

const ResetPassword = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { showAlertError, showAlertSuccess } = useAlert();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const normalizedToken = useMemo(() => String(token || '').trim(), [token]);

  const onSubmit = async () => {
    if (submitting) {
      return;
    }

    if (!normalizedToken) {
      showAlertError('Invalid password reset link.');
      return;
    }

    if (!password || password.length < 6) {
      showAlertError('Password must be at least 6 characters long.');
      return;
    }

    if (password !== confirmPassword) {
      showAlertError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);
      await userService.resetPasswordWithToken(normalizedToken, password);
      showAlertSuccess('Password has been reset. Please sign in with your new password.');
      navigate('/signin');
    } catch (error) {
      showAlertError(
        error?.response?.data ||
          error?.message ||
          'Unable to reset password. Your link may be invalid or expired.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AuthTemplate title="Choose a new password" subtitle="This reset link expires in 60 minutes.">
      <Stack spacing={1.5}>
        <Input
          label="New Password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
        />
        <Input
          label="Confirm Password"
          type="password"
          autoComplete="new-password"
          value={confirmPassword}
          onChange={(event) => setConfirmPassword(event.target.value)}
        />

        <Box display="flex" flexDirection="column" alignItems="center" gap={1.5}>
          <PrimaryButton
            fullWidth
            variant="contained"
            color="primary"
            size="large"
            disabled={submitting}
            onClick={onSubmit}>
            {submitting ? 'Resetting...' : 'Reset Password'}
          </PrimaryButton>
          {submitting ? <CircularProgress size={18} /> : null}
        </Box>

        <Divider sx={{ m: 1 }} />

        <PrimaryButton fullWidth variant="outlined" color="info" onClick={() => navigate('/signin')}>
          Back to Sign In
        </PrimaryButton>
      </Stack>
    </AuthTemplate>
  );
};

export default ResetPassword;
